import {useQuery} from "@apollo/client";
import classNames from "classnames";
import {useMemo} from "react";
import {useFormContext} from "react-hook-form";

import {Button} from "@/components/button/Button.tsx";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {FormLabel} from "@/components/form/FormLabel";
import {Loader} from "@/components/placeholder/Loader.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {CloudStatusGroupList, GQL_THIRD_PARTY_GROUPS, ThirdPartyService} from "@/lib/queries/checks.ts";
import {KEY_TAB} from "@/wcag.ts";

import styles from "./ThirdyPartyPopularGroups.module.scss";

interface ThirdyPartyPopularGroupsProps {
  onChange: (group: ThirdPartyService | null, setGroup?: boolean) => void;
}

export const ThirdyPartyPopularGroups = ({onChange}: ThirdyPartyPopularGroupsProps) => {
  const {watch} = useFormContext();
  const selectedGroup = watch(ServiceField.THIRD_PARTY_GROUP) as ThirdPartyService | null;
  const {data, loading, previousData} = useQuery<CloudStatusGroupList>(GQL_THIRD_PARTY_GROUPS, {
    variables: {limit: 12, orderBy: "popular"},
  });
  const currentData = loading ? previousData : data;
  const groups = useMemo(() => currentData?.cloudStatusGroups.items || [], [currentData]);
  const isLoading = loading && groups.length === 0;

  return (
    <>
      <FormLabel forId="popular_service_name" className="mt-2 mb-3">
        Popular Services
      </FormLabel>
      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Loader />
        </div>
      ) : (
        <div className={classNames("row row-cols-1 row-cols-sm-2 row-cols-md-3 g-3", styles.popularGroupsContainer)}>
          {groups.map((group: ThirdPartyService) => {
            return (
              <div className="col" key={"popular-third-party-" + group.id}>
                <Button
                  onClick={() => onChange(group, true)}
                  onKeyDown={(e) => {
                    if (e.key !== KEY_TAB) {
                      onChange(group, true);
                    }
                  }}
                  variant="cloudStatusS"
                  className={classNames({active: group.id === selectedGroup?.id})}
                >
                  <CDNImage className={styles.popularGroupIcon} alt={`${group.group} logo`} src={group.logo_src} />
                  <div className="pulseTextBodySmallBold">{group.group}</div>
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <Divider className="mt-5" />
    </>
  );
};
